import { __ } from '@wordpress/i18n';
import { 
    useBlockProps,
    useInnerBlocksProps,
    BlockControls,
    InspectorControls,
} from '@wordpress/block-editor';
import { 
    Panel,
    PanelBody,
    __experimentalToggleGroupControl as ToggleGroupControl,
    __experimentalToggleGroupControlOption as ToggleGroupControlOption,
    __experimentalNumberControl as NumberControl,
} from '@wordpress/components';
import { useEffect, useRef, useCallback } from '@wordpress/element';
import SingleBlockTypeAppender from '../utils/appender';

export default function Edit(props) {
    const { attributes, setAttributes, clientId } = props;

    const ALLOWED_BLOCKS = ['wpopus/accordion-item'];
    const TEMPLATE = [['wpopus/accordion-item']];
    const blocksProps = useBlockProps({ className: `wpopus-accordion ${attributes.options.iconPlacement || 'left-icon'}` });
    const { children, ...innerBlocksProps } = useInnerBlocksProps(blocksProps, {
        allowedBlocks: ALLOWED_BLOCKS,
        template: TEMPLATE,
    });

    const setMetaOption = (key, value) => {
        setAttributes({
            options: {
                ...attributes.options,
                [key]: value,
            },
        });
    };

    const accordion = useRef(null);

    // Callback to handle click events and toggle active class
    const handleAccordionClick = useCallback((item) => {
        if (accordion.current) {
            const accordionItems = accordion.current.querySelectorAll('.wpopus-accordion-item');
            accordionItems.forEach((accordionItem) => {
                accordionItem.classList.remove("active");
            });
            item.classList.toggle("active");
        }
    }, []);

    useEffect(() => {
        if (!accordion.current) return;

        const accordionItems = accordion.current.querySelectorAll('.wpopus-accordion-item');
        accordionItems.forEach((item) => {
            const heading = item.querySelector('.accordion-heading');
            if (heading) {
                heading.addEventListener('click', () => handleAccordionClick(item));
            }
        });

        return () => {
            accordionItems.forEach((item) => {
                const heading = item.querySelector('.accordion-heading');
                if (heading) {
                    heading.removeEventListener('click', () => handleAccordionClick(item));
                }
            });
        };
    }, [handleAccordionClick, children, blocksProps]);

    return (
        <div {...innerBlocksProps}>
            <div
                ref={accordion}
                className='wpopus-accordion-wrapper'
                style={{
                    "--wpopus-accordion-item-gap": `${attributes.options.gap || 0}px`,
                }}
            >
                {children}
            </div>

            <InspectorControls key="setting">
                <Panel>
                    <PanelBody title={__('Accordion Settings', 'wpopus')} initialOpen={true}>
                        <fieldset>
                            <ToggleGroupControl
                                __nextHasNoMarginBottom={true}
                                label={__('Icon Placement', 'wpopus')}
                                value={attributes.options.iconPlacement || 'left-icon'}
                                isBlock
                                onChange={(newValue) => setMetaOption('iconPlacement', newValue)}
                            >
                                <ToggleGroupControlOption value="left-icon" label={__('Left', 'wpopus')} />
                                <ToggleGroupControlOption value="right-icon" label={__('Right', 'wpopus')} />
                            </ToggleGroupControl>
                        </fieldset>

                        <fieldset>
                            <NumberControl
                                label={__('Accordion Items Gap', 'wpopus')}
                                labelPosition="top"
                                step={1}
                                max={100}
                                min={0}
                                value={attributes.options.gap || 0}
                                onChange={(newValue) => setMetaOption('gap', newValue)}
                            />
                        </fieldset>
                    </PanelBody>
                </Panel>
            </InspectorControls>

            <BlockControls>
                <SingleBlockTypeAppender
                    isPrimary
                    buttonText={__('Add New Accordion Item', 'wpopus')}
                    allowedBlock="wpopus/accordion-item"
                    clientId={clientId}
                />
            </BlockControls>
        </div>
    );
}
